import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from './lib/adminApi';
import { Layout } from './components/Layout';

const AdminLogin = lazy(() => import('./pages/AdminLogin').then((m) => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import('./pages/AdminUsers').then((m) => ({ default: m.AdminUsers })));
const AdminWeeklyReports = lazy(() => import('./pages/AdminWeeklyReports').then((m) => ({ default: m.AdminWeeklyReports })));
const AdminBroadcasts = lazy(() => import('./pages/AdminBroadcasts').then((m) => ({ default: m.AdminBroadcasts })));
const AdminGuestAnalytics = lazy(() => import('./pages/AdminGuestAnalytics').then((m) => ({ default: m.AdminGuestAnalytics })));
const AdminBehaviour = lazy(() => import('./pages/AdminBehaviour').then((m) => ({ default: m.AdminBehaviour })));
const AdminPlanner = lazy(() => import('./pages/AdminPlanner').then((m) => ({ default: m.AdminPlanner })));
const AdminTemplates = lazy(() => import('./pages/AdminTemplates').then((m) => ({ default: m.AdminTemplates })));
const AdminTemplateEdit = lazy(() => import('./pages/AdminTemplates').then((m) => ({ default: m.AdminTemplateEdit })));
const AdminExpenseCategories = lazy(() => import('./pages/AdminExpenseCategories').then((m) => ({ default: m.AdminExpenseCategories })));
const AdminStories = lazy(() => import('./pages/AdminStories').then((m) => ({ default: m.AdminStories })));
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements').then((m) => ({ default: m.AdminAnnouncements })));

function Fallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--ground)', color: 'var(--ink-3)', fontSize: 14 }}>
      Загрузка...
    </div>
  );
}

function AdminApp() {
  const [isAuthorized, setAuthorized] = useState<boolean>(() => Boolean(adminApi.getToken()));
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    const checkToken = async () => {
      if (!adminApi.getToken()) return;
      try {
        await adminApi.getStats();
        setAuthorized(true);
      } catch {
        adminApi.clearToken();
        setAuthorized(false);
        if (!isLoginPage) navigate('/login', { replace: true });
      }
    };
    checkToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    adminApi.clearToken();
    setAuthorized(false);
    navigate('/login');
  };

  if (!isAuthorized && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route path="/login" element={<AdminLogin onLoginSuccess={() => setAuthorized(true)} />} />
        <Route element={<Layout onLogout={handleLogout} />}>
          <Route path="/" element={<AdminDashboard />} />
          <Route path="/users" element={<AdminUsers />} />
          <Route path="/weekly-reports" element={<AdminWeeklyReports />} />
          <Route path="/analytics" element={<AdminGuestAnalytics />} />
          <Route path="/behaviour" element={<AdminBehaviour />} />
          <Route path="/planner" element={<AdminPlanner />} />
          <Route path="/broadcasts" element={<AdminBroadcasts />} />
          <Route path="/templates" element={<AdminTemplates />} />
          <Route path="/templates/:id" element={<AdminTemplateEdit />} />
          <Route path="/expense-categories" element={<AdminExpenseCategories />} />
          <Route path="/stories" element={<AdminStories />} />
          <Route path="/announcements" element={<AdminAnnouncements />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AdminApp />
    </BrowserRouter>
  );
}
